/**
 * Deload Detector
 *
 * After each completed session, checks whether any primary muscle group
 * has exceeded MRV (20 hard sets) for 2+ consecutive weeks.
 * If so, sets settings.deloadRecommended = true.
 *
 * The generator reads this flag and reduces weights by 50% and volume by 50%
 * for the next session, then auto-clears it.
 *
 * Pure function — takes DB data, returns a recommendation flag.
 */

import { db } from '@/lib/db';
import type { MuscleGroup } from '@/types';

const MRV = 20; // Maximum Recoverable Volume (hard sets/week)

/** Returns weekly hard-set counts per muscle for a given week offset (0 = current, 1 = last week) */
async function weeklyVolumeForOffset(weekOffset: number): Promise<Map<MuscleGroup, number>> {
  const now = Date.now();
  const weekMs = 7 * 24 * 3_600_000;
  const end   = new Date(now - weekOffset * weekMs).toISOString();
  const start = new Date(now - (weekOffset + 1) * weekMs).toISOString();

  const sets = await db.loggedSets
    .where('loggedAt').between(start, end)
    .filter((s) => !s.isWarmup && s.rpe >= 7)
    .toArray();

  const exIds = [...new Set(sets.map((s) => s.exerciseId))];
  const exercises = await db.exercises.bulkGet(exIds);
  const exMap = new Map(exercises.filter(Boolean).map((e) => [e!.id, e!]));

  const vol = new Map<MuscleGroup, number>();
  for (const s of sets) {
    const ex = exMap.get(s.exerciseId);
    if (!ex) continue;
    for (const alloc of ex.muscles) {
      vol.set(alloc.muscle, (vol.get(alloc.muscle) ?? 0) + alloc.fraction);
    }
  }
  return vol;
}

export async function checkDeloadNeeded(): Promise<boolean> {
  const [thisWeek, lastWeek] = await Promise.all([
    weeklyVolumeForOffset(0),
    weeklyVolumeForOffset(1),
  ]);

  // Check if any muscle exceeded MRV both weeks
  for (const [muscle, vol] of thisWeek) {
    const lastVol = lastWeek.get(muscle) ?? 0;
    if (vol >= MRV && lastVol >= MRV) {
      console.info(`[DeloadDetector] ${muscle} exceeded MRV (${vol.toFixed(1)}/${MRV}) for 2 weeks`);
      return true;
    }
  }
  return false;
}

/** Apply deload: reduce all muscle state fatigue and set the deload flag */
export async function applyDeloadIfNeeded(): Promise<void> {
  const needed = await checkDeloadNeeded();
  if (!needed) return;

  await db.settings.update('singleton', {
    deloadRecommended: true,
    lastDeloadAt: new Date().toISOString(),
  });
}
